import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/api-client';
import { useRouter } from '@tanstack/react-router';
import { getStoredUser, storeAuth, getStoredTokens } from '@/lib/auth';
import { ROUTES } from '@/lib/constants';
import { UserRole } from '@academia-apolo/shared-types';
import { EditorialField, EditorialSubmit, EditorialAlert, editorialInputStyle, } from './EditorialField';
function getErrorMessage(code) {
    const messages = {
        AUTH_WRONG_CURRENT_PASSWORD: 'La contraseña actual es incorrecta.',
        AUTH_PASSWORD_TOO_WEAK: 'La nueva contraseña no cumple los requisitos de seguridad.',
        AUTH_PASSWORD_SAME_AS_CURRENT: 'La nueva contraseña no puede ser igual a la actual.',
    };
    return messages[code] ?? 'Ha ocurrido un error. Inténtalo de nuevo.';
}
function getDefaultRoute(role) {
    switch (role) {
        case UserRole.DIRECTOR:
            return ROUTES.DIRECTOR_HOME;
        case UserRole.PROFESSOR:
            return ROUTES.PROFESSOR_HOME;
        case UserRole.SUPPORT:
            return ROUTES.SUPPORT_INBOX;
        default:
            return ROUTES.STUDENT_DASHBOARD;
    }
}
export function ChangePasswordForm() {
    const [serverError, setServerError] = useState(null);
    const router = useRouter();
    const changePwd = useMutation({
        mutationFn: (data) => apiClient('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify(data),
        }),
        onSuccess: () => {
            const user = getStoredUser();
            const tokens = getStoredTokens();
            if (user && tokens) {
                storeAuth(tokens, { ...user, mustChangePassword: false });
            }
            const role = user?.role ?? UserRole.STUDENT;
            void router.navigate({ to: getDefaultRoute(role) });
        },
        onError: (err) => {
            if (err instanceof ApiError) {
                setServerError(getErrorMessage(err.code));
            }
            else {
                setServerError('Ha ocurrido un error. Inténtalo de nuevo.');
            }
        },
    });
    const { register, handleSubmit, watch, formState: { errors }, } = useForm();
    const newPassword = watch('newPassword');
    function onSubmit(values) {
        setServerError(null);
        changePwd.mutate({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
        });
    }
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-8", noValidate: true, children: [_jsx(EditorialField, { label: "Contrase\u00F1a actual", index: "01", htmlFor: "cp-current", error: errors.currentPassword?.message, children: _jsx("input", { id: "cp-current", type: "password", autoComplete: "current-password", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", style: editorialInputStyle, ...register('currentPassword', {
                        required: 'La contraseña actual es obligatoria',
                    }) }) }), _jsx(EditorialField, { label: "Nueva contrase\u00F1a", index: "02", htmlFor: "cp-new", error: errors.newPassword?.message, hint: "M\u00EDnimo 8 caracteres, al menos 1 may\u00FAscula y 1 n\u00FAmero", children: _jsx("input", { id: "cp-new", type: "password", autoComplete: "new-password", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", style: editorialInputStyle, ...register('newPassword', {
                        required: 'La nueva contraseña es obligatoria',
                        minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                        pattern: {
                            value: /^(?=.*[A-Z])(?=.*\d).+$/,
                            message: 'Debe incluir al menos 1 mayúscula y 1 número',
                        },
                    }) }) }), _jsx(EditorialField, { label: "Confirmar nueva contrase\u00F1a", index: "03", htmlFor: "cp-confirm", error: errors.confirmPassword?.message, children: _jsx("input", { id: "cp-confirm", type: "password", autoComplete: "new-password", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", style: editorialInputStyle, ...register('confirmPassword', {
                        required: 'Confirma la nueva contraseña',
                        validate: (val) => val === newPassword || 'Las contraseñas no coinciden',
                    }) }) }), serverError && _jsx(EditorialAlert, { message: serverError }), _jsx(EditorialSubmit, { loading: changePwd.isPending, label: "Cambiar contrase\u00F1a", loadingLabel: "Guardando\u2026" })] }));
}
//# sourceMappingURL=ChangePasswordForm.js.map